<template>
  <SmartLinkItemComponent
    custom-tag="div"
    class="faq-page"
    :id="page.system.id"
    :component="!page.system.workflow"
  >
    <!-- Hero -->
    <!-- ADD Seo metatag for background image -->
    <img v-if="backgroundImageUrl" :src="backgroundImageUrl" :alt="backgroundImageAlt" style="display: none" />
    <div
      class="hero-block"
      :style="{ backgroundImage: `url('${backgroundImageUrl}')` }"
      :title="backgroundImageAlt"
    >
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <h1 v-if="data.title" class="title">
              <SmartLinkStandaloneElement :codename="typ.elements.title.codename">
                {{ data.title }}
              </SmartLinkStandaloneElement>
            </h1>
            <p v-if="data.subTitle" class="subtitle col-lg-6">
              <SmartLinkStandaloneElement :codename="typ.elements.sub_title.codename">
                {{ data.subTitle }}
              </SmartLinkStandaloneElement>
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6">
            <div class="searcher">
              <input
                type="text"
                aria-label="Page Search"
                ref="pageSearch"
                v-model="searchText"
                :placeholder="data.placeholderText"
                class="form-control form-control-lg"
                @keyup="search"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Result from search -->
    <div ref="searchResultContainer" class="container page-search-results-container">
      <div class="row">
        <div class="col">
          <div
            ref="pageSearchResults"
            :data-results-hdr="$t('dataResultsHdr')"
            :data-no-results-msg="$t('dataNoResultsMsg')"
          >
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="container">
      <div class="row">
        <div class="col-lg-4" v-if="data.tableOfContents">
          <!-- Table of Contents -->
          <SmartLinkStandaloneElement
            custom-tag="div"
            class="table-contents"
            :codename="typ.elements.table_of_contents.codename"
          >
            <div class="collapse-toc-container" v-if="data.tableOfContents">
              <button class="btn btn-outline-primary" @click="collapseExpand()">
                <font-awesome class="icon" icon="arrows-up-down" />
                {{ isExpanded ? $t('collapseAll') : $t('expandAll') }}
              </button>
            </div>
            <h2 class="title">{{$t('tableOfContents')}}</h2>
            <div class="scrollable-wrapper">
              <ul class="content-list">
                <li
                  :class="`content-item ${selectedId == list.system.id ? 'active' : ''}`"
                  @click="setId(list.system.id)"
                  v-for="list in data.accordionLists"
                >
                  <NuxtLink
                    :to="`#${list.system.id}`"
                    @click.prevent="scrollToElement(list.system.id)"
                  >
                    {{ list.elements.title.value }}
                  </NuxtLink>
                </li>
              </ul>
            </div>
          </SmartLinkStandaloneElement>
        </div>
        <client-only>
        <!-- Accordion List -->
        <SmartLinkStandaloneElement
          custom-tag="div"
          class="col-lg"
          :codename="typ.elements.accordion_lists.codename"
        >
          <div class="collapse-accordion-container" v-if="!data.tableOfContents">
            <button class="btn btn-outline-primary" @click="collapseExpand()">
              <font-awesome class="icon" icon="arrows-up-down" />
              {{ isExpanded ? $t('collapseAll') : $t('expandAll') }}
            </button>
          </div>
            <AccordionList v-for="list in data.accordionLists" :model="list" :options="options">
            </AccordionList>
        </SmartLinkStandaloneElement>
        </client-only>
      </div>
    </div>
  </SmartLinkItemComponent>
</template>

<script setup lang="ts">
import type { AccordionListModel, FrequentlyAskedQuestionsModel } from '~/models'
import type { AccordionOptions } from '~/models/options/accordionOptions'
import { firstOrDefault } from '~/utils/first-or-default'
import { isEmpty } from '~/utils/kontent-helper'
import { contentTypes } from '~/models'
import debounce from '~/utils/debounce'
const typ = contentTypes.frequently_asked_questions

const i18n = useI18n()

const { scrollToElement } = useSmoothScroll()
const pageStore = usePageStore()
const { $bootstrap } = useNuxtApp()
const page = pageStore.page as FrequentlyAskedQuestionsModel
const searchText = ref('')
const selectedId = ref('')
function setId(id: string) {
  selectedId.value = id
}

const data = {
  title: page.elements.title.value,
  subTitle: page.elements.subTitle.value,
  placeholderText: page.elements.searchPlaceholderText.value,
  accordionLists: pageStore.getListAs<AccordionListModel>(page.elements.accordionLists.value),
  tableOfContents: multipleChoiceToBoolean(page.elements.tableOfContents)
}

selectedId.value = firstOrDefault(data.accordionLists)?.system.id ?? ''

const options = {
  numbering: multipleChoiceToBoolean(page.elements.numbering),
  startExpanded: multipleChoiceToBoolean(page.elements.stateOnPageLoad),
  fromFaq: true
} as AccordionOptions

const siteStore = useSiteStore()

const backgroundImageUrl = computed(() => {
  return siteStore.defaultBackgroundHeroImageUrl
})

const backgroundImageAlt = computed(() => {
  return siteStore.defaultBackgroundHeroImageAlt
})

const route = useRoute()

const pageSearchResults = ref<HTMLDivElement | null>(null)
const searchResultContainer = ref<HTMLDivElement | null>(null)

onMounted(() => {
  search()

    const targetId = route.hash.replace('#', '')
    if (targetId) {
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' })
        const collapseExpand = document.getElementById(`d-${targetId}`)
        const collapseInstance = $bootstrap.Collapse.getOrCreateInstance(collapseExpand)
        collapseInstance.show()
      }
    }
})

const isExpanded = ref(options.startExpanded)
function collapseExpand() {
  const buttons = document.querySelectorAll('.accordion-button')
  const accordions = document.querySelectorAll('.accordion-collapse')

  if (isExpanded.value) {
    buttons.forEach((element) => {
      element.classList.add('collapsed')
    })

    accordions.forEach((element) => {
      element.classList.remove('show')
    })
  } else {
    buttons.forEach((element) => {
      element.classList.remove('collapsed')
    })

    accordions.forEach((element) => {
      element.classList.add('show')
    })
  }

  isExpanded.value = !isExpanded.value
}

function search() {
  if (searchText.value.length < 1) {
    clear()
  } else {
    const debouncedSearch = debounce(functionNameSearch, 300)
    debouncedSearch()
  }
}

function functionNameSearch() {
  const searchVal = searchText.value.toLowerCase()
  searchList(searchVal)
}

const searchResHdr = computed(
  () => pageSearchResults.value?.getAttribute('data-results-hdr') || i18n.t('dataResultsHdr')
)
const noResultsMsg = computed(
  () => pageSearchResults.value?.getAttribute('data-no-results-msg') || i18n.t('dataNoResultsMsg')
)

function searchList(searchVal: string) {
  searchVal = searchVal.toLowerCase()
  const questionMatches: any[] = []

  if (searchResultContainer.value) {
    searchResultContainer.value.classList.add('d-block')
  }

  let lastIndx = 0
  document.querySelectorAll('.accordion-list').forEach((accordion) => {
    const acc = accordion as HTMLElement

    acc.querySelectorAll('.accordion').forEach(function (ac) {
      const qResult = ac.querySelectorAll('.accordion-item')
      qResult.forEach(function (li, key) {
        const question = li.querySelector('div.accordion-header > button') as HTMLElement
        const answer = li.querySelector('.accordion-body') as HTMLElement
        const combinedText = question.innerText + ' ' + answer.innerText
        const id = key + 1 + lastIndx
        const collapseExpand = document.getElementById(`d-q-${id}`)

        if (collapseExpand) {
          const collapseInstance = $bootstrap.Collapse.getOrCreateInstance(collapseExpand)
          if (searchVal && combinedText.toLowerCase().includes(searchVal)) {
            questionMatches.push({
              Question: question.getAttribute('id'),
              Label: question.textContent
            })
            collapseInstance.show()
          } else {
            collapseInstance.hide()
          }
        }
      })

      lastIndx = qResult.length + lastIndx
    })
  })

  let resultsHtml
  if (questionMatches.length > 0) {
    const qResultsHtml = questionMatches
      .map(function (match) {
        const parts = match.Label.split('.')
        const str = parts.join('. ')
        return `<li><a href="#${match.Question}" class="scroll-link">${str}</a></li>`
      })
      .join('')

    resultsHtml = `<p>${searchResHdr.value}</p><ul>${qResultsHtml}</ul>`
  } else {
    resultsHtml = `<p>${noResultsMsg.value}</p>`
  }

  if (pageSearchResults.value) {
    // eslint-disable-next-line no-unsanitized/property
    pageSearchResults.value.innerHTML = useSanitize(resultsHtml, 'html')

    pageSearchResults.value.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        const href = (link as HTMLAnchorElement).getAttribute('href') || ''
        scrollToElement(href.replace(/^#/, ''))
      })
    })
  }
}

function clear() {
  if (searchResultContainer.value) {
    searchResultContainer.value.classList.remove('d-block')
  }

  const buttons = document.querySelectorAll('.accordion-button')
  const accordions = document.querySelectorAll('.accordion-collapse')

  buttons.forEach((element) => {
    element.classList.add('collapsed')
  })

  accordions.forEach((element) => {
    element.classList.remove('show')
  })
}
</script>

<style lang="scss" scoped>
h2.title {
  white-space: nowrap;
}

.page-search-results-container {
  display: none;
}

.d-block {
  display: block;
}

.collapse-accordion-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.collapse-toc-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 28px;
}
</style>
